import { type UseMutationOptions, useMutation } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { backendService } from "@/services/backend.service";

interface InitiateRoboCallParams {
	requestId: string;
}

type InitiateRoboCallResponse = AxiosResponse<unknown>;

type InitiateRoboCallOptions = Omit<UseMutationOptions<InitiateRoboCallResponse, Error, InitiateRoboCallParams, unknown>, "mutationFn">;

export const useInitiateRoboCall = (options?: InitiateRoboCallOptions) => {
	const { t } = useTranslation();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();

	return useMutation({
		...options,
		mutationFn: (params: InitiateRoboCallParams) => {
			track("robocall_initiated", {
				request_id: params.requestId,
				department_id: chosenDepartment.department_id,
			});
			return backendService.createCallOffAgent(params.requestId, chosenDepartment.department_id);
		},
		throwOnError: false,
		onError: (error, variables) => {
			showErrorSnackbar(
				t("robocall.initiate.error"),
				{
					request_id: variables.requestId,
					department_id: chosenDepartment.department_id,
				},
				error,
			);
		},
		onSuccess: (_, variables) => {
			track("robocall_initiated_success", {
				request_id: variables.requestId,
				department_id: chosenDepartment.department_id,
			});
			showSnackbar(t("robocall.initiate.success"), "success");
		},
	});
};
